import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Progress, Card, Row, Col, Statistic, Select, Radio } from 'antd';
import { ArrowLeftOutlined, TrophyOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { makeGetCall } from '../../utils/helper';
import moment from 'moment';

const { Option } = Select;

type Population = 'enrolled' | 'native';

const UserScores = ({ quiz, onBack }) => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [labels, setLabels] = useState<string[]>([]);
  const [labelFilter, setLabelFilter] = useState<string | null>(null);
  // Default to 'enrolled' per manager decision 2026-05-27 — the dashboard's
  // primary audience is program-enrolled users; native scoring is the
  // secondary view.
  const [population, setPopulation] = useState<Population>('enrolled');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalQuestions: 0,
    avgScore: 0,
    topScore: 0,
    maxMarks: 0
  });

  useEffect(() => {
    if (quiz?.quiz_id) {
      fetchScores();
      fetchLabels();
    }
  }, [quiz]);

  useEffect(() => {
    if (quiz?.quiz_id) {
      fetchScores();
    }
  }, [labelFilter, population]);

  const fetchLabels = async () => {
    try {
      const response = await makeGetCall(`/quiz/labels?quizId=${quiz.quiz_id}`);
      setLabels(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch labels:', error);
    }
  };

  const fetchScores = async () => {
    setLoading(true);
    try {
      const base = labelFilter
        ? `/quiz/scores-by-label?quizId=${quiz.quiz_id}&label=${encodeURIComponent(labelFilter)}`
        : `/quiz/scores?quizId=${quiz.quiz_id}`;
      const url = `${base}&population=${population}`;

      const response = await makeGetCall(url);
      const scoresData = response.data.data || [];
      setScores(scoresData);

      // Calculate statistics
      const totalUsers = scoresData.length;
      const totalQuestions = scoresData.reduce((sum, s) => sum + s.score_total_questions, 0);

      const avgMarks = totalUsers > 0
        ? scoresData.reduce((sum, s) => sum + (s.score_total_marks || 0), 0) / totalUsers
        : 0;

      const topMarks = scoresData.length > 0
        ? Math.max(...scoresData.map(s => s.score_total_marks || 0))
        : 0;

      const maxPossibleMarks = scoresData.length > 0
        ? scoresData[0].score_max_marks || 0
        : 0;

      setStats({
        totalUsers,
        totalQuestions,
        avgScore: avgMarks.toFixed(1),
        topScore: topMarks,
        maxMarks: maxPossibleMarks
      });
    } catch (error) {
      console.error('Failed to fetch scores:', error);
    }
    setLoading(false);
  };

  const columns = [
    {
      title: 'Rank',
      key: 'rank',
      width: '8%',
      align: 'center',
      render: (_, __, index) => {
        if (index === 0) return <Tag color="gold">🥇 1st</Tag>;
        if (index === 1) return <Tag color="silver">🥈 2nd</Tag>;
        if (index === 2) return <Tag color="bronze">🥉 3rd</Tag>;
        return <Tag>{index + 1}</Tag>;
      }
    },
    {
      title: 'User',
      key: 'user',
      width: '25%',
      render: (_, record) => (
        <div>
          <div className="font-semibold">{record.user?.user_fullname || 'Unknown'}</div>
          <div className="text-gray-500 text-sm">
            {record.user?.username} - {record.user?.user_type}
          </div>
        </div>
      ),
    },
    {
      title: 'Questions Answered',
      dataIndex: 'score_total_questions',
      key: 'score_total_questions',
      width: '15%',
      align: 'center',
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      title: 'Correct Answers',
      dataIndex: 'score_correct_answers',
      key: 'score_correct_answers',
      width: '12%',
      align: 'center',
      render: (count) => <Tag color="green">{count}</Tag>
    },
    {
      title: 'Marks Obtained',
      dataIndex: 'score_total_marks',
      key: 'marks',
      width: '13%',
      align: 'center',
      sorter: (a, b) => (a.score_total_marks || 0) - (b.score_total_marks || 0),
      defaultSortOrder: 'descend',
      render: (_, record) => {
        const marks = record.score_total_marks || 0;
        const maxMarks = record.score_max_marks || 0;
        const percentage = parseFloat(record.score_percentage) || 0;
        let color = 'red';
        if (percentage >= 80) color = 'green';
        else if (percentage >= 60) color = 'orange';

        return (
          <div>
            <div className="font-bold text-lg" style={{ color }}>
              {marks} / {maxMarks}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Percentage',
      dataIndex: 'score_percentage',
      key: 'percentage',
      width: '12%',
      align: 'center',
      sorter: (a, b) => (parseFloat(a.score_percentage) || 0) - (parseFloat(b.score_percentage) || 0),
      render: (_, record) => {
        const percentage = parseFloat(record.score_percentage) || 0;
        let color = 'red';
        if (percentage >= 80) color = 'green';
        else if (percentage >= 60) color = 'orange';

        return (
          <div className="font-bold" style={{ color }}>
            {percentage.toFixed(2)}%
          </div>
        );
      }
    },
    {
      title: 'Last Updated',
      dataIndex: 'score_last_updated',
      key: 'score_last_updated',
      width: '12%',
      render: (date) => moment(date).format('DD MMM YYYY')
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} className="mb-2">
          Back to Questions
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{quiz?.quiz_name} - Scores & Statistics</h2>
            <p className="text-gray-500">View user performance for this quiz</p>
          </div>
          <div className="flex items-center gap-3">
            <Radio.Group
              value={population}
              onChange={(e) => setPopulation(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="enrolled">Enrolled users</Radio.Button>
              <Radio.Button value="native">Native users</Radio.Button>
            </Radio.Group>
            {labels.length > 0 && (
              <Select
                placeholder="Filter by label"
                allowClear
                value={labelFilter}
                onChange={setLabelFilter}
                style={{ width: 200 }}
              >
                {labels.map(l => (
                  <Option key={l} value={l}>{l}</Option>
                ))}
              </Select>
            )}
          </div>
        </div>
      </div>

      <Row gutter={16} className="mb-4">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Respondents"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Questions Answered"
              value={stats.totalQuestions}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Average Marks"
              value={stats.avgScore}
              suffix={`/ ${stats.maxMarks}`}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Top Marks"
              value={stats.topScore}
              suffix={`/ ${stats.maxMarks}`}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={scores}
        loading={loading}
        rowKey={(record: any) => record.score_id ?? `${population}-${record.score_user_id}`}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
          position: ['bottomCenter']
        }}
      />
    </div>
  );
};

export default UserScores;
